import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FileText, Music, Video, Link2, Upload } from "lucide-react";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

const TYPE_OPTIONS = [
  { key: "pdf", label: "PDF", icon: FileText, accept: ".pdf,application/pdf", limitMb: 20 },
  { key: "audio", label: "Ses", icon: Music, accept: "audio/*", limitMb: 50 },
  { key: "video", label: "Video", icon: Video, accept: "video/*", limitMb: 200 },
];

export default function AdminResourceEditor() {
  const navigate = useNavigate();
  const [type, setType] = useState("pdf");
  const [videoMode, setVideoMode] = useState("upload"); // "upload" | "link"
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const activeType = TYPE_OPTIONS.find((t) => t.key === type);
  const usingExternalLink = type === "video" && videoMode === "link";

  const uploadFile = async () => {
    const { data: initRes } = await api.post("/resources/uploads/init", {
      title: title.trim(),
      description: description.trim(),
      type,
      file_name: file.name,
      mime_type: file.type,
      total_size: file.size,
      cover_image_url: coverImageUrl.trim(),
    });
    const uploadId = initRes.upload_id;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const blob = file.slice(start, start + CHUNK_SIZE);
      const form = new FormData();
      form.append("chunk_index", i);
      form.append("chunk", blob, `chunk_${i}`);
      await api.post(`/resources/uploads/${uploadId}/chunk`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProgress(Math.round(((i + 1) / totalChunks) * 100));
    }
    const completeForm = new FormData();
    completeForm.append("total_chunks", totalChunks);
    await api.post(`/resources/uploads/${uploadId}/complete`, completeForm, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!usingExternalLink && !file) {
      setError("Lütfen bir dosya seç.");
      return;
    }
    if (usingExternalLink && !externalUrl.trim()) {
      setError("Lütfen bir link gir.");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      if (usingExternalLink) {
        await api.post("/resources/external", {
          title: title.trim(),
          description: description.trim(),
          external_url: externalUrl.trim(),
          cover_image_url: coverImageUrl.trim(),
        });
      } else {
        await uploadFile();
      }
      toast.success("Kaynak eklendi");
      navigate("/kaynaklar");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-container py-12 md:py-16">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-5xl text-ink-900" data-testid="resource-editor-title">
          Yeni kaynak
        </h1>

        <form onSubmit={submit} className="k-card mt-8 space-y-5 p-8" data-testid="resource-editor-form">
          <div>
            <label className="k-label">Tür</label>
            <div className="flex gap-2">
              {TYPE_OPTIONS.map((t) => (
                <button
                  type="button"
                  key={t.key}
                  onClick={() => {
                    setType(t.key);
                    setFile(null);
                  }}
                  className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold ${
                    type === t.key
                      ? "border-sage-500 bg-sage-500 text-cream-50"
                      : "border-cream-300 bg-cream-50 text-ink-700"
                  }`}
                  data-testid={`resource-type-${t.key}`}
                >
                  <t.icon className="mr-1.5 inline h-4 w-4" /> {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="k-label">Başlık</label>
            <input
              className="k-input"
              required
              minLength={2}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="resource-title-input"
            />
          </div>
          <div>
            <label className="k-label">Açıklama (opsiyonel)</label>
            <textarea
              rows={3}
              className="k-input resize-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="resource-description-input"
            />
          </div>

          <div>
            <label className="k-label">Kapak görseli URL (opsiyonel)</label>
            <input
              className="k-input"
              placeholder="https://…"
              value={coverImageUrl}
              onChange={(e) => setCoverImageUrl(e.target.value)}
              data-testid="resource-cover-image-input"
            />
            {coverImageUrl && (
              <img
                src={coverImageUrl}
                alt="Kapak önizleme"
                className="mt-2 h-32 w-full rounded-xl object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}
          </div>

          {type === "video" && (
            <div>
              <label className="k-label">Video kaynağı</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setVideoMode("upload")}
                  className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold ${
                    videoMode === "upload"
                      ? "border-sage-500 bg-sage-500 text-cream-50"
                      : "border-cream-300 bg-cream-50 text-ink-700"
                  }`}
                  data-testid="video-mode-upload"
                >
                  <Upload className="mr-1.5 inline h-4 w-4" /> Dosya yükle
                </button>
                <button
                  type="button"
                  onClick={() => setVideoMode("link")}
                  className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold ${
                    videoMode === "link"
                      ? "border-sage-500 bg-sage-500 text-cream-50"
                      : "border-cream-300 bg-cream-50 text-ink-700"
                  }`}
                  data-testid="video-mode-link"
                >
                  <Link2 className="mr-1.5 inline h-4 w-4" /> Harici link
                </button>
              </div>
            </div>
          )}

          {usingExternalLink ? (
            <div>
              <label className="k-label">Video linki</label>
              <input
                className="k-input"
                placeholder="https://youtube.com/… veya https://drive.google.com/…"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                data-testid="resource-external-url-input"
              />
            </div>
          ) : (
            <div>
              <label className="k-label">Dosya (en fazla {activeType.limitMb}MB)</label>
              <input
                type="file"
                accept={activeType.accept}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="k-input"
                data-testid="resource-file-input"
              />
              {file && (
                <p className="mt-1 font-body text-xs text-ink-500">
                  {file.name} · {(file.size / (1024 * 1024)).toFixed(1)}MB
                </p>
              )}
            </div>
          )}

          {busy && !usingExternalLink && (
            <div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-cream-200">
                <div
                  className="h-full rounded-full bg-sage-500 transition-all"
                  style={{ width: `${progress}%` }}
                  data-testid="upload-progress-bar"
                />
              </div>
              <p className="mt-1 font-body text-xs text-ink-500">Yükleniyor… %{progress}</p>
            </div>
          )}

          {error && (
            <div
              className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              data-testid="resource-editor-error"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={busy}
              className="k-btn-primary disabled:opacity-60"
              data-testid="resource-submit-btn"
            >
              {busy ? "Kaydediliyor…" : "Kaynağı yayınla"}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="k-btn-ghost">
              Vazgeç
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
